/**
 * AdHealthWidget — T1 «Ad health strip (calendar)»
 * ===================================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, T1.
 *
 * Ряд из 10 KPI-плиток (значение последнего полного дня окна, Δ к значению
 * 7 дней назад, 30-дневная спарклайн-серия) — «сдвинулась ли рекламная
 * выручка и какой рычаг виноват», одним взглядом. Плитка становится
 * янтарной при ухудшении больше 15% (падение, а для «чем меньше, тем лучше» — рост). Ниже — линия четырёх мультипликативных рычагов
 * (DAU, доля вовлечённых, показы/вовлечённого, eCPM), индексированных к 100
 * на первый день окна дашборда (единая ось).
 *
 * Диапазон дат дашборда трактуется КАЛЕНДАРНО (event_date), не как когорта —
 * см. AdsService/adsCommon.
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { Badge } from '../../ui/badge.jsx';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer as RechartsResponsiveContainer } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog.jsx';
import { Maximize2 } from 'lucide-react';
import { ads as adsSpecs } from '@ggstat/chart-specs';

const LEVER_COLORS = Object.fromEntries(adsSpecs.health.levers.map((lever) => [lever.key, lever.color]));

const SPARK_COLOR = adsSpecs.health.sparkColor;
const AMBER_COLOR = adsSpecs.health.amberColor;

function fmtValue(value, unit) {
  const v = Number(value || 0);
  switch (unit) {
    case 'usd':
      return v.toLocaleString('ru-RU', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    case 'percent':
      return `${(v * 100).toFixed(1)}%`;
    case 'ratio':
      return v.toFixed(2);
    default:
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
  }
}

/**
 * Тултип спарклайна: дата периода + значение. Без XAxis с dataKey recharts
 * отдаёт в label индекс точки, а не дату, поэтому дату берём из самой точки.
 */
function SparkTooltip({ active, payload, unit, metricLabel }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-background border rounded-lg shadow-lg px-2 py-1 text-xs space-y-0.5">
      <p className="font-medium">{point.date}</p>
      <p>
        {metricLabel}: {fmtValue(point.value, unit)}
      </p>
    </div>
  );
}

// Доля досмотра колеблется в пределах долей процента — ей нужна крупная плитка
// с осью Y по диапазону данных, иначе сдвиг в 0.5% не виден.
const LARGE_TILE_KEYS = new Set(['showToComplete']);

function fmtAxis(value, unit) {
  const v = Number(value || 0);
  if (unit === 'percent') return `${(v * 100).toFixed(1)}%`;
  if (unit === 'ratio') return v.toFixed(2);
  return v.toLocaleString('ru-RU', { notation: 'compact', maximumFractionDigits: 1 });
}

/** Ось Y по диапазону данных с небольшим запасом — видны малые изменения. */
function tightDomain([dataMin, dataMax]) {
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return [0, 1];
  const pad = (dataMax - dataMin) * 0.1 || Math.abs(dataMax) * 0.01 || 1;
  return [dataMin - pad, dataMax + pad];
}

/** Крупный график метрики: полные оси, сетка, ось Y по диапазону данных. */
function DetailChart({ points, unit, label, color, height }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey="date" tick={{ fill: 'currentColor' }} className="text-xs" minTickGap={16} />
        <YAxis
          domain={tightDomain}
          tick={{ fill: 'currentColor' }}
          className="text-xs"
          width={64}
          tickFormatter={(v) => fmtAxis(v, unit)}
        />
        <Tooltip content={<SparkTooltip unit={unit} metricLabel={label} />} />
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function ExpandButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Открыть крупно: ${label}`}
      title="Открыть крупно"
      className="text-muted-foreground hover:text-foreground p-0.5 rounded"
    >
      <Maximize2 className="h-3.5 w-3.5" />
    </button>
  );
}

function fmtDelta(deltaPct) {
  if (deltaPct == null) return '—';
  const pct = deltaPct * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

/**
 * Плитка KPI: значение, Δ к значению 7 дней назад, спарклайн 30 дней.
 * Янтарный фон/бордер — при ухудшении больше 15% с учётом lowerIsBetter (tile.isAmber от сервера).
 */
function AdHealthTile({ tile, onExpand }) {
  const points = tile.sparkline || [];
  const isLarge = LARGE_TILE_KEYS.has(tile.key);
  const color = tile.isAmber ? AMBER_COLOR : SPARK_COLOR;
  const isDown = tile.deltaPct != null && tile.deltaPct < 0;
  // Для метрик, где рост — плохо (доля ошибок), направление цвета обратное
  const isBad = tile.lowerIsBetter ? tile.deltaPct != null && tile.deltaPct > 0 : isDown;

  return (
    <div
      className={
        'rounded-lg border p-3 flex flex-col gap-2 ' +
        (isLarge ? 'col-span-2 ' : '') +
        (tile.isAmber ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : 'border-border')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
          {tile.label}
          <ExpandButton onClick={() => onExpand(tile)} label={tile.label} />
        </span>
        {tile.deltaPct != null && (
          <Badge
            variant="outline"
            className={
              tile.isAmber
                ? 'border-amber-500 text-amber-700 dark:text-amber-400'
                : isBad
                ? 'border-red-400 text-red-600 dark:text-red-400'
                : 'border-emerald-400 text-emerald-600 dark:text-emerald-400'
            }
          >
            Δ7д {fmtDelta(tile.deltaPct)}
          </Badge>
        )}
      </div>

      <span className="text-xl font-semibold">{fmtValue(tile.value, tile.unit)}</span>

      {isLarge ? (
        <DetailChart points={points} unit={tile.unit} label={tile.label} color={color} height={180} />
      ) : (
      <div className="h-10 -mx-1">
        <RechartsResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
            <defs>
              <linearGradient id={`spark-${tile.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={tile.isAmber ? AMBER_COLOR : SPARK_COLOR} stopOpacity={0.35} />
                <stop offset="100%" stopColor={tile.isAmber ? AMBER_COLOR : SPARK_COLOR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <Tooltip content={<SparkTooltip unit={tile.unit} metricLabel={tile.label} />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={tile.isAmber ? AMBER_COLOR : SPARK_COLOR}
              fill={`url(#spark-${tile.key})`}
              strokeWidth={1.5}
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </RechartsResponsiveContainer>
      </div>
      )}
    </div>
  );
}

/** Собирает точки всех рычагов в один массив строк {date, dau, engagedShare, ...} для одной оси X. */
function buildLeverRows(levers) {
  if (!levers || levers.length === 0) return [];
  const byDate = new Map();
  for (const lever of levers) {
    for (const point of lever.points) {
      const row = byDate.get(point.date) || { date: point.date };
      row[lever.key] = point.value;
      byDate.set(point.date, row);
    }
  }
  return Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
}

function LeverTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {Number(entry.value).toFixed(1)}
        </p>
      ))}
    </div>
  );
}

/**
 * Линия рычагов, индексированных к 100 на первый день окна: DAU, доля
 * вовлечённых, показы/вовлечённого, eCPM — единая ось, легенда и прямые
 * подписи (см. скилл dataviz).
 */
function AdHealthLeversChart({ levers, height = 260, onExpand }) {
  const rows = buildLeverRows(levers);
  if (rows.length === 0) return null;

  return (
    <div>
      {onExpand && (
        <p className="text-sm font-medium mb-2 flex items-center gap-1">
          Рычаги выручки, индекс к 100 на начало диапазона
          <ExpandButton onClick={onExpand} label="Рычаги выручки" />
        </p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="date" tick={{ fill: 'currentColor' }} className="text-xs" />
          <YAxis
            tick={{ fill: 'currentColor' }}
            className="text-xs"
            label={{ value: 'Индекс (100 = старт окна)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<LeverTooltip />} />
          <Legend />
          {levers.map((lever) => (
            <Line
              key={lever.key}
              type="monotone"
              dataKey={lever.key}
              name={lever.label}
              stroke={LEVER_COLORS[lever.key] || '#64748b'}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

async function fetchAdHealth(filters, options) {
  return AdsService.get('/health', filters, {}, options);
}

export function AdHealthWidget() {
  const healthData = useAdsWidgetData(fetchAdHealth);
  const widgetProps = useBaseWidget(healthData);
  const { data } = widgetProps;

  const tiles = data?.tiles || [];
  const levers = data?.levers || [];
  // null | { kind: 'tile', key } | { kind: 'levers' }
  const [expanded, setExpanded] = React.useState(null);
  const expandedTile = expanded?.kind === 'tile' ? tiles.find((t) => t.key === expanded.key) : null;

  const isDialogOpen = expanded != null && (expanded.kind === 'levers' || expandedTile != null);

  return (
    <>
    <BaseWidget
      {...widgetProps}
      title="Здоровье рекламной монетизации"
      subtitle="Выручка, вовлечённость, частота и цена показа — календарно, по дням"
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={6}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {tiles.map((tile) => (
            <AdHealthTile key={tile.key} tile={tile} onExpand={(t) => setExpanded({ kind: 'tile', key: t.key })} />
          ))}
        </div>

        <AdHealthLeversChart levers={levers} onExpand={() => setExpanded({ kind: 'levers' })} />
      </div>
    </BaseWidget>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && setExpanded(null)}>
        <DialogContent className="sm:max-w-5xl w-[95vw]">
          <DialogHeader>
            <DialogTitle>{expandedTile ? expandedTile.label : 'Рычаги выручки'}</DialogTitle>
            <DialogDescription>
              {expandedTile
                ? `Последний день: ${fmtValue(expandedTile.value, expandedTile.unit)} · Δ7д ${fmtDelta(expandedTile.deltaPct)}`
                : 'Индекс к 100 на начало диапазона'}
            </DialogDescription>
          </DialogHeader>
          {expandedTile && (
            <DetailChart
              points={expandedTile.sparkline || []}
              unit={expandedTile.unit}
              label={expandedTile.label}
              color={expandedTile.isAmber ? AMBER_COLOR : SPARK_COLOR}
              height={460}
            />
          )}
          {expanded?.kind === 'levers' && <AdHealthLeversChart levers={levers} height={460} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdHealthWidget;
